import {
  LogOut,
  LogIn,
  User,
  HelpCircle,
  Plus,
  Compass,
  Heart,
  ClipboardList,
  Route,
  Search,
  UserPlus,
  Globe,
  BookOpen,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '../shadcn';
import { useRepository } from '../../core/api';
import { useAuth, useIsAuthenticated } from '../../core/auth';
import { useTranslation } from 'react-i18next';
import { useScrollDirection } from '../../lib/hooks/use-scroll-direction';
import { cn } from '../../lib/shadcn/utils';
import { useQuery } from '@tanstack/react-query';

// Types
interface NavItem {
  href: string;
  icon: React.ReactNode;
  label: string;
}

/** Blog index. Article routes under /blog stay highlighted with the same icon. */
const BLOG_HREF = '/blog';

function isBlogPath(pathname: string): boolean {
  return pathname === '/blog' || pathname.startsWith('/blog/');
}

// Mobile bottom nav items
const mobileNavItems = [
  { icon: Globe, label: 'Explore', href: '/' },
  { icon: Route, label: 'All Itineraries', href: '/itineraries' },
  { icon: Plus, label: 'Create', href: '/itineraries/create' },
  { icon: Search, label: 'Search', href: '/search' },
  { icon: User, label: 'Profile', href: '/profile' },
];

// Components
const Logo = () => (
  <Link to="/" className="flex-shrink-0">
    <img
      src={window.location.origin + '/logo_only.webp'}
      alt="CitySage Logo"
      className="h-9 w-auto"
    />
  </Link>
);

const MobileNavbar = ({
  isAuthenticated,
  handleLogin,
  handleSignUp,
}: {
  isAuthenticated: boolean;
  handleLogin: () => void;
  handleSignUp: () => void;
}) => {
  return (
    <nav
      className="block md:hidden z-50 bg-white w-full border-b border-gray-100"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="mx-auto px-2 py-2">
        <div className="flex items-center justify-between gap-1">
          <Link
            to="/"
            className="flex shrink-0 items-center"
            aria-label="CitySage"
          >
            <img
              src={window.location.origin + '/logo_only.webp'}
              alt="CitySage"
              className="h-6 w-auto"
            />
          </Link>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to={BLOG_HREF}
              className="px-1.5 py-1 text-sm font-medium text-foreground"
            >
              Blog
            </Link>

            {/* Help Icon */}
            <Link to="/help">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <HelpCircle className="h-4 w-4" />
              </Button>
            </Link>

            {isAuthenticated && (
              <Link to="/itineraries/create">
                <Button size="sm" variant="outline" className="h-8 px-3">
                  <Plus className="h-4 w-4 mr-1" />
                  <span className="text-xs">Create</span>
                </Button>
              </Link>
            )}

            {/* Auth Buttons */}
            {!isAuthenticated && (
              <div className="flex items-center gap-1">
                <Button
                  onClick={handleSignUp}
                  variant="default"
                  size="sm"
                  className="h-8 px-2"
                >
                  <UserPlus className="h-4 w-4 mr-1" />
                  <span className="text-xs">Sign Up</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const MobileBottomNav = () => {
  const location = useLocation();

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 block md:hidden"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="flex">
        {mobileNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;

          return (
            <Link
              key={item.label}
              to={item.href}
              className="flex-1 flex items-center justify-center py-2 px-1"
            >
              <Icon
                className={cn(
                  'h-5 w-5',
                  isActive ? 'text-primary' : 'text-gray-400'
                )}
              />
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

const DesktopSidebar = ({
  isAuthenticated,
  handleLogin,
  handleSignUp,
  handleLogout,
}: {
  isAuthenticated: boolean;
  handleLogin: () => void;
  handleSignUp: () => void;
  handleLogout: () => void;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { api } = useRepository();
  const { userEmail } = useAuth();

  // Fetch account data when authenticated
  const { data: account } = useQuery({
    queryKey: ['account'],
    queryFn: () => api.getMyAccount(),
    enabled: isAuthenticated && !!userEmail,
  });

  const handleProfileClick = () => {
    navigate({ to: '/profile' });
  };

  const { i18n } = useTranslation();

  const sidebarNavItems = [
    { icon: Globe, label: t('navigation.explore', 'Explore'), href: '/' },
    {
      icon: Route,
      label: t('navigation.allItineraries', 'All Itineraries'),
      href: '/itineraries',
    },
    { icon: Search, label: t('navigation.search', 'Search'), href: '/search' },
    { icon: BookOpen, label: t('navigation.blog', 'Blog'), href: BLOG_HREF },
    { icon: HelpCircle, label: t('common.buttons.help'), href: '/help' },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch', comingSoon: true },
    { code: 'fr', name: 'Français', comingSoon: true },
  ];

  return (
    <aside className="fixed left-0 top-0 h-full w-16 bg-card border-r border-border z-40 hidden md:flex flex-col shadow-sm">
      {/* Logo */}
      <div className="flex flex-col items-center justify-center py-4 border-b border-border">
        <Link to="/" className="flex-shrink-0 flex flex-col items-center gap-1">
          <img
            src={window.location.origin + '/logo_only.webp'}
            alt="CitySage Logo"
            className="h-8 w-8 rounded-lg"
          />
          <span className="text-[10px] font-medium text-primary leading-tight whitespace-nowrap">
            CitySage
          </span>
        </Link>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 flex flex-col items-center py-4 space-y-2">
        {sidebarNavItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            item.href === BLOG_HREF
              ? isBlogPath(location.pathname)
              : location.pathname === item.href;

          return (
            <Link
              key={item.label}
              to={item.href}
              className={cn(
                'relative group flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
            >
              <Icon className="h-6 w-6" />

              {/* Tooltip */}
              <div className="absolute left-16 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {item.label}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Bottom Section - Create, Language, Profile */}
      <div className="flex flex-col items-center py-4 space-y-2 border-t border-border">
        {/* Create Button */}
        <Link to="/itineraries/create" className="relative group">
          <div
            className={cn(
              'flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200',
              location.pathname === '/itineraries/create'
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
          >
            <Plus className="h-6 w-6" />
          </div>
          {/* Tooltip */}
          <div className="absolute left-16 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            {t('navigation.create', 'Create')}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
          </div>
        </Link>

        {/* Language Switch */}
        <div className="relative group">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-12 w-12 rounded-xl p-0"
              >
                <Globe className="h-6 w-6" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start">
              {languages.map((language) => (
                <DropdownMenuItem
                  key={language.code}
                  onClick={() =>
                    !language.comingSoon && i18n.changeLanguage(language.code)
                  }
                  className={cn(
                    i18n.language === language.code ? 'bg-accent' : '',
                    language.comingSoon && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{language.name}</span>
                    {language.comingSoon && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (coming soon)
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Tooltip */}
          <div className="absolute left-16 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            Language
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
          </div>
        </div>

        {/* Auth Section */}
        {isAuthenticated ? (
          <div className="relative group">
            <Link to="/profile">
              <Button
                variant="ghost"
                size="sm"
                className="relative h-12 w-12 rounded-xl p-0"
              >
                <Avatar className="h-10 w-10">
                  <AvatarImage
                    src={account?.avatarUrl}
                    alt={account?.userName || 'User'}
                  />
                  <AvatarFallback className="bg-muted text-muted-foreground">
                    {account?.userName?.slice(0, 2).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </Link>
            {/* Tooltip */}
            <div className="absolute left-16 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              {account?.userName || 'User'}
              <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <div className="relative group">
              <Button
                onClick={handleSignUp}
                variant="default"
                size="sm"
                className="h-10 w-10 p-0 rounded-xl"
              >
                <UserPlus className="h-5 w-5" />
              </Button>
              {/* Tooltip */}
              <div className="absolute left-16 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Sign Up
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
              </div>
            </div>
            <div className="relative group">
              <Button
                onClick={handleLogin}
                variant="outline"
                size="sm"
                className="h-10 w-10 p-0 rounded-xl"
              >
                <LogIn className="h-5 w-5" />
              </Button>
              {/* Tooltip */}
              <div className="absolute left-16 top-1/2 -translate-y-1/2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-lg border opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {t('common.buttons.login')}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-popover border-l border-b rotate-45"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

// Main Navbar Component
export function Navbar() {
  const isAuthenticated = useIsAuthenticated();
  const { t } = useTranslation();
  const { login, signIn, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.log('Logout error:', error);
    }
  };

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.log('Login error:', error);
    }
  };

  const handleSignUp = () => {
    signIn();
  };

  return (
    <>
      {/* Mobile Top Navbar */}
      <MobileNavbar
        isAuthenticated={isAuthenticated}
        handleLogin={handleLogin}
        handleSignUp={handleSignUp}
      />

      {/* Mobile Bottom Navbar */}
      <MobileBottomNav />

      {/* Desktop Sidebar */}
      <DesktopSidebar
        isAuthenticated={isAuthenticated}
        handleLogin={handleLogin}
        handleSignUp={handleSignUp}
        handleLogout={handleLogout}
      />
    </>
  );
}
